import { useCallback, useEffect, useState } from 'react'
import { Calculator, MapPin, Plus, Power, Trash2 } from 'lucide-react'
import { formatarReais, ModoTaxa, Papel } from '@du/shared'
import {
  Badge, Button, Divider, Escolha, GrupoEscolhas, Icone, Input, SectionTitle, Skeleton, StatTile, useAviso,
} from '@du/ui'
import { api, ErroApi } from '../lib/api.js'
import { paraCentavos, paraTexto } from '../lib/dinheiro.js'
import { useSessao } from '../lib/sessao.js'

type Bairro = { id: string; nome: string; taxaCents: number; tempoEstimadoMin: number; ativo: boolean }
/** O que a rota de simulação devolve. Montado a partir dela, não adivinhado. */
type Simulacao = {
  modoTaxa: string
  distanciaKm: number | null
  bairro: string | null
  lojaTemCoordenada: boolean
  resultado:
    | { ok: true; taxaCents: number; tempoEstimadoMin: number; origem: string; faixaAteKm?: number }
    | { ok: false; codigo: string; mensagem: string }
}
type Faixa = { ateKm: number; taxaCents: number; tempoEstimadoMin: number }
type LojaDados = {
  id: string
  nome: string
  abertaManual: boolean
  fechadaManual: boolean
  deliveryPausado: boolean
  modoTaxa: ModoTaxa
  taxaPadraoCents: number
  tempoPadraoMin: number
  tempoPreparoMin: number
  pedidoMinimoCents: number
  raioMaximoKm: number | null
  lat: number | null
  lng: number | null
  imprimirAo: 'RECEBER' | 'ACEITAR'
  avisarStatusZap: boolean
  aceitarAutoWhatsapp: boolean
  chavePix: string | null
  fidelidadeAtiva: boolean
  fidelidadePontosPorReal: number
  fidelidadeAtivaDesde: string | null
  faixasEntrega: Faixa[]
  abertaAgora: { delivery: boolean; balcao: boolean }
}

export function Loja() {
  const { usuario } = useSessao()
  const { avisar } = useAviso()
  const [loja, setLoja] = useState<LojaDados | null>(null)
  const [bairros, setBairros] = useState<Bairro[]>([])
  const [faixas, setFaixas] = useState<Faixa[]>([])
  const [novoBairro, setNovoBairro] = useState({ nome: '', taxa: '', tempo: '45' })
  const [novaFaixa, setNovaFaixa] = useState({ ateKm: '', taxa: '', tempo: '45' })
  const [simulacao, setSimulacao] = useState<Simulacao | null>(null)
  const [kmTeste, setKmTeste] = useState('')

  const gestor = usuario?.papel === Papel.ADMIN || usuario?.papel === Papel.GERENTE

  const carregar = useCallback(async () => {
    try {
      const [l, b] = await Promise.all([api<LojaDados>('/v1/loja'), api<Bairro[]>('/v1/bairros')])
      setLoja(l)
      setBairros(b)
      setFaixas(l.faixasEntrega)
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Não consegui carregar a loja', detalhe: e instanceof ErroApi ? e.message : undefined })
    }
  }, [avisar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const mexer = async (campos: Partial<LojaDados>, aviso?: string) => {
    if (!loja) return
    const antes = loja
    setLoja({ ...loja, ...campos })
    try {
      await api('/v1/loja', { method: 'PATCH', corpo: campos })
      if (aviso) avisar({ tom: 'sucesso', texto: aviso })
      await carregar()
    } catch (e) {
      setLoja(antes)
      avisar({ tom: 'erro', texto: 'Não consegui salvar', detalhe: e instanceof ErroApi ? e.message : undefined })
    }
  }

  const salvarFaixas = async (lista: Faixa[]) => {
    try {
      await api('/v1/faixas-entrega', { method: 'PUT', corpo: { faixas: lista } })
      setFaixas(lista)
      avisar({ tom: 'sucesso', texto: 'Faixas de entrega salvas' })
    } catch (e) {
      // O servidor recusa faixa duplicada e faixa que barateia com a
      // distância; a mensagem dele já diz qual é o problema.
      avisar({ tom: 'erro', texto: 'Faixas recusadas', detalhe: e instanceof ErroApi ? e.message : undefined })
      await carregar()
    }
  }

  /**
   * Pergunta ao SERVIDOR quanto o cliente pagaria — nunca calcula aqui.
   *
   * A conta é a mesma do checkout. Uma cópia da regra no navegador daria um
   * número bonito na tela e diferente do que o cliente vê na hora de pagar,
   * que é o pior defeito possível numa tela de preço.
   */
  const simular = async (corpo: { bairroId?: string; distanciaKm?: number }) => {
    try {
      setSimulacao(
        await api<Simulacao>('/v1/entrega/simular', {
          method: 'POST',
          corpo: { tipo: 'ENTREGA', ...corpo },
        }),
      )
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Não consegui calcular', detalhe: e instanceof ErroApi ? e.message : undefined })
    }
  }

  /**
   * A resposta da simulação, em português.
   *
   * Fica numa variável porque é mostrada em DOIS lugares — junto da lista de
   * bairros e junto da calculadora de distância —, e a resposta tem de nascer
   * onde a pergunta foi feita. Na primeira versão ela só existia no alto da
   * seção, e quem clicava em Calcular lá embaixo não via nada acontecer.
   */
  const resposta = simulacao ? (
        <p
          className={
            simulacao.resultado.ok ? 'nota-alerta nota-alerta--ok' : 'nota-alerta nota-alerta--erro'
          }
        >
          {simulacao.resultado.ok ? (
            <>
              {/*
                Duas frases inteiras, em vez de uma com buraco no meio:
                encaixar "grátis" onde cabia "R$ 7,00" produzia "o cliente
                paga nada — entrega grátis de entrega".
              */}
              {simulacao.bairro
                ? `No bairro ${simulacao.bairro}, `
                : `A ${String(simulacao.distanciaKm ?? 0).replace('.', ',')} km, `}
              {simulacao.resultado.taxaCents === 0 ? (
                <>
                  a entrega é <strong>grátis</strong>
                </>
              ) : (
                <>
                  o cliente paga <strong>{formatarReais(simulacao.resultado.taxaCents)}</strong> de
                  entrega
                </>
              )}
              , com previsão de {simulacao.resultado.tempoEstimadoMin} minutos.
              {simulacao.resultado.origem === 'PADRAO'
                ? ' (Caiu na taxa padrão — nenhuma faixa nem bairro cobriu este caso.)'
                : simulacao.resultado.faixaAteKm != null
                  ? ` (Faixa de até ${simulacao.resultado.faixaAteKm} km.)`
                  : ''}
            </>
          ) : (
            <>O pedido NÃO fecha: {simulacao.resultado.mensagem}</>
          )}
        </p>
  ) : null

  if (!loja) return <div className="pagina-gestao"><Skeleton altura={240} /></div>

  return (
    <div className="pagina-gestao">
      {/* ── Estado do turno ───────────────────────────────────────────── */}
      <section className="bloco-gestao">
        <SectionTitle variante="laranja" tamanho="sm">Agora</SectionTitle>
        <div className="grade-stats">
          <StatTile
            rotulo="Delivery"
            valor={loja.abertaAgora.delivery ? 'Aberto' : 'Fechado'}
            apoio={loja.deliveryPausado ? 'pausado manualmente' : 'pelo horário'}
            tom={loja.abertaAgora.delivery ? 'marca' : 'neutro'}
          />
          <StatTile
            rotulo="Balcão"
            valor={loja.abertaAgora.balcao ? 'Aberto' : 'Fechado'}
            tom={loja.abertaAgora.balcao ? 'marca' : 'neutro'}
          />
        </div>
        <div className="linha-acoes">
          <Button
            variante={loja.fechadaManual ? 'primario' : 'perigo'}
            icone={Power}
            onClick={() => void mexer({ fechadaManual: !loja.fechadaManual }, loja.fechadaManual ? 'Loja reaberta' : 'Loja fechada')}
          >
            {loja.fechadaManual ? 'Reabrir loja' : 'Fechar agora'}
          </Button>
          <Button
            variante="secundario"
            onClick={() => void mexer({ deliveryPausado: !loja.deliveryPausado }, loja.deliveryPausado ? 'Entregas retomadas' : 'Entregas pausadas')}
          >
            {loja.deliveryPausado ? 'Retomar entregas' : 'Pausar entregas'}
          </Button>
          <Button
            variante="ghost"
            onClick={() => void mexer({ abertaManual: !loja.abertaManual })}
          >
            {loja.abertaManual ? 'Voltar ao horário' : 'Abrir fora do horário'}
          </Button>
        </div>
        {loja.abertaManual ? (
          <p className="nota-alerta">
            A loja está aberta fora do horário. Lembre de voltar ao horário no fim do turno,
            senão ela aceita pedido de madrugada.
          </p>
        ) : null}
      </section>

      {/* ── Entrega ───────────────────────────────────────────────────── */}
      <section className="bloco-gestao">
        <SectionTitle variante="amarelo" tamanho="sm">Entrega</SectionTitle>

        <GrupoEscolhas nome="Como calcular a taxa" min={1} max={1} obrigatorio escolhidos={1}>
          <Escolha
            tipo="radio" nome="modo" valor={ModoTaxa.BAIRRO} rotulo="Por bairro"
            descricao="Taxa fixa por bairro. Previsível, não depende de GPS."
            marcada={loja.modoTaxa === ModoTaxa.BAIRRO}
            desabilitada={!gestor}
            onMudar={() => void mexer({ modoTaxa: ModoTaxa.BAIRRO })}
          />
          <Escolha
            tipo="radio" nome="modo" valor={ModoTaxa.HIBRIDO} rotulo="Por raio, com bairro de reserva"
            descricao="Cobra pela distância; sem coordenada confiável, cai no bairro. Recomendado."
            marcada={loja.modoTaxa === ModoTaxa.HIBRIDO}
            desabilitada={!gestor}
            onMudar={() => void mexer({ modoTaxa: ModoTaxa.HIBRIDO })}
          />
          <Escolha
            tipo="radio" nome="modo" valor={ModoTaxa.RAIO} rotulo="Só por raio"
            descricao="Endereço que o mapa não localizar BLOQUEIA o pedido."
            marcada={loja.modoTaxa === ModoTaxa.RAIO}
            desabilitada={!gestor}
            onMudar={() => void mexer({ modoTaxa: ModoTaxa.RAIO })}
          />
        </GrupoEscolhas>

        {loja.modoTaxa !== ModoTaxa.BAIRRO && !loja.lat ? (
          <p className="nota-alerta">
            A loja ainda não tem coordenada cadastrada — sem ela não dá para medir distância,
            e o modo por raio sempre cai na reserva.
          </p>
        ) : null}

        {/*
          A seção de bairros só aparece quando o modo USA bairro. No modo "só
          por raio" ela é ruído: o dono olhava uma lista vazia com um aviso
          alarmante enquanto a entrega funcionava normalmente pelas faixas.
        */}
        {loja.modoTaxa !== ModoTaxa.RAIO ? (
        <>
        <h3 className="sub">
          Bairros
          {loja.modoTaxa === ModoTaxa.HIBRIDO ? (
            <span className="sub__apoio"> — só entram em ação quando o mapa não localiza o endereço</span>
          ) : null}
        </h3>
        {bairros.length === 0 ? (
          <p className="nota-alerta">
            {loja.modoTaxa === ModoTaxa.BAIRRO
              ? 'Nenhum bairro cadastrado. Nesse modo o checkout de entrega não fecha — o cliente só consegue pedir para retirada.'
              : `Nenhum bairro cadastrado. Quando o mapa não conseguir localizar o endereço, a entrega sai por ${formatarReais(loja.taxaPadraoCents)} (a taxa padrão). Cadastrar bairros aqui dá um preço melhor para esses casos.`}
          </p>
        ) : (
          <ul className="lista-simples">
            {bairros.map((b) => (
              <li key={b.id}>
                <span><Icone de={MapPin} tamanho={16} /> {b.nome}</span>
                <span>{formatarReais(b.taxaCents)} · {b.tempoEstimadoMin} min</span>
                <span className="lista-simples__acoes">
                  <Button variante="ghost" tamanho="sm" icone={Calculator} onClick={() => void simular({ bairroId: b.id })} aria-label={`Simular ${b.nome}`} />
                  {gestor ? (
                    <Button
                      variante="ghost" tamanho="sm" icone={Trash2}
                      aria-label={`Excluir ${b.nome}`}
                      onClick={async () => {
                        try {
                          await api(`/v1/bairros/${b.id}`, { method: 'DELETE' })
                          await carregar()
                        } catch (e) {
                          avisar({ tom: 'erro', texto: 'Não consegui excluir', detalhe: e instanceof ErroApi ? e.message : undefined })
                        }
                      }}
                    />
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* O resultado aparece junto do controle que o pediu — ver `resposta`. */}
        {simulacao && simulacao.bairro !== null ? resposta : null}

        {gestor ? (
          <div className="form-linha">
            <Input rotulo="Bairro" value={novoBairro.nome} onChange={(e) => setNovoBairro({ ...novoBairro, nome: e.target.value })} />
            <Input rotulo="Taxa" prefixo="R$" placeholder="6,00" inputMode="decimal" value={novoBairro.taxa} onChange={(e) => setNovoBairro({ ...novoBairro, taxa: e.target.value })} />
            <Input rotulo="Minutos" inputMode="numeric" value={novoBairro.tempo} onChange={(e) => setNovoBairro({ ...novoBairro, tempo: e.target.value })} />
            <Button
              icone={Plus}
              onClick={async () => {
                const taxa = paraCentavos(novoBairro.taxa)
                if (!novoBairro.nome.trim() || taxa === null) {
                  avisar({ tom: 'erro', texto: 'Preencha nome e taxa' })
                  return
                }
                try {
                  await api('/v1/bairros', {
                    method: 'POST',
                    corpo: { nome: novoBairro.nome.trim(), taxaCents: taxa, tempoEstimadoMin: Number(novoBairro.tempo) || 45 },
                  })
                  setNovoBairro({ nome: '', taxa: '', tempo: '45' })
                  await carregar()
                } catch (e) {
                  avisar({ tom: 'erro', texto: 'Não consegui criar', detalhe: e instanceof ErroApi ? e.message : undefined })
                }
              }}
            >
              Adicionar
            </Button>
          </div>
        ) : null}

        </>
        ) : null}

        {/* As faixas só aparecem quando o modo mede distância. */}
        {loja.modoTaxa !== ModoTaxa.BAIRRO ? (
        <>
        {loja.modoTaxa === ModoTaxa.HIBRIDO ? <Divider tom="sutil" /> : null}
        <h3 className="sub">Preço por distância</h3>

        {/*
          O teste existia só para bairro — e numa loja sem bairro nenhum,
          ficava inalcançável. Aqui ele responde a pergunta que de fato se
          faz ao mexer nas faixas: "e a quantos km fica quanto?".
        */}
        <div className="form-linha">
          <Input
            rotulo="Quanto fica a entrega a quantos km?"
            inputMode="decimal"
            placeholder="3"
            dica="Responde com o mesmo cálculo que o cliente vê no checkout"
            value={kmTeste}
            onChange={(e) => setKmTeste(e.target.value)}
          />
          <Button
            variante="secundario"
            icone={Calculator}
            onClick={() => {
              const km = Number(kmTeste.replace(',', '.'))
              if (!Number.isFinite(km) || km < 0) {
                avisar({ tom: 'erro', texto: 'Digite a distância em km' })
                return
              }
              void simular({ distanciaKm: km })
            }}
          >
            Calcular
          </Button>
        </div>
        {simulacao && simulacao.bairro === null ? resposta : null}

        {faixas.length === 0 ? (
          <p className="nota-alerta">
            Nenhuma faixa cadastrada.
            {loja.modoTaxa === ModoTaxa.RAIO
              ? ' Sem elas, nenhum pedido de entrega fecha.'
              : ` Sem elas, toda entrega sai por ${formatarReais(loja.taxaPadraoCents)} (a taxa padrão).`}
          </p>
        ) : (
          <ul className="lista-simples">
            {faixas.map((f, i) => (
              <li key={f.ateKm}>
                {/*
                  "de X até Y km" em vez de só "até Y km": a faixa anterior é
                  que define onde esta começa, e sem isso ninguém sabe qual
                  faixa pega qual endereço.
                */}
                <span>
                  {i === 0 ? 'até' : `de ${faixas[i - 1]!.ateKm} a`} {f.ateKm} km
                  {loja.raioMaximoKm !== null && f.ateKm > loja.raioMaximoKm ? (
                    <Badge tom="erro">acima do limite de {loja.raioMaximoKm} km</Badge>
                  ) : null}
                </span>
                <span>
                  {f.taxaCents === 0 ? <strong>Grátis</strong> : formatarReais(f.taxaCents)} ·{' '}
                  {f.tempoEstimadoMin} min
                </span>
                {gestor ? (
                  <span className="lista-simples__acoes">
                    <Button
                      variante="ghost" tamanho="sm" icone={Trash2}
                      aria-label={`Remover faixa até ${f.ateKm} km`}
                      onClick={() => void salvarFaixas(faixas.filter((x) => x.ateKm !== f.ateKm))}
                    />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {gestor ? (
          <div className="form-linha">
            <Input rotulo="Até quantos km" inputMode="decimal" placeholder="2" value={novaFaixa.ateKm} onChange={(e) => setNovaFaixa({ ...novaFaixa, ateKm: e.target.value })} />
            {/*
              A entrega grátis JÁ existia: é uma faixa com taxa zero. Só não
              estava escrito em lugar nenhum, e o exemplo no campo dizia
              "5,00" — então ninguém descobria sozinho.
            */}
            <Input
              rotulo="Taxa"
              prefixo="R$"
              placeholder="5,00"
              inputMode="decimal"
              dica="0 = entrega grátis nessa distância"
              value={novaFaixa.taxa}
              onChange={(e) => setNovaFaixa({ ...novaFaixa, taxa: e.target.value })}
            />
            <Input rotulo="Minutos" inputMode="numeric" value={novaFaixa.tempo} onChange={(e) => setNovaFaixa({ ...novaFaixa, tempo: e.target.value })} />
            <Button
              icone={Plus}
              onClick={() => {
                const km = Number(novaFaixa.ateKm.replace(',', '.'))
                const taxa = paraCentavos(novaFaixa.taxa)
                if (!Number.isFinite(km) || km <= 0 || taxa === null) {
                  avisar({ tom: 'erro', texto: 'Preencha a distância e a taxa' })
                  return
                }
                void salvarFaixas([
                  ...faixas.filter((f) => f.ateKm !== km),
                  { ateKm: km, taxaCents: taxa, tempoEstimadoMin: Number(novaFaixa.tempo) || 45 },
                ].sort((a, b) => a.ateKm - b.ateKm))
                setNovaFaixa({ ateKm: '', taxa: '', tempo: '45' })
              }}
            >
              Adicionar
            </Button>
          </div>
        ) : null}
        </>
        ) : null}
      </section>

      {/* ── Operação ──────────────────────────────────────────────────── */}
      {gestor ? (
        <section className="bloco-gestao">
          <SectionTitle variante="preto" tamanho="sm">Operação</SectionTitle>
          <div className="form-linha">
            <Input
              rotulo="Tempo de preparo (min)" inputMode="numeric"
              defaultValue={String(loja.tempoPreparoMin)}
              onBlur={(e) => {
                const v = Number(e.target.value)
                if (v >= 5 && v !== loja.tempoPreparoMin) void mexer({ tempoPreparoMin: v }, 'Tempo de preparo salvo')
              }}
            />
            <Input
              rotulo="Pedido mínimo" prefixo="R$" inputMode="decimal"
              defaultValue={paraTexto(loja.pedidoMinimoCents)}
              dica="0 = sem mínimo"
              onBlur={(e) => {
                const v = paraCentavos(e.target.value)
                if (v !== null && v !== loja.pedidoMinimoCents) void mexer({ pedidoMinimoCents: v }, 'Pedido mínimo salvo')
              }}
            />
          </div>
          {/*
            A chave Pix é para onde o dinheiro do cliente vai. Fica aqui, em
            branco por padrão e digitada pela loja: deduzir de outro campo
            mandaria o Pix do cliente para a conta errada, e isso não volta.
          */}
          <Input
            rotulo="Chave Pix da loja"
            defaultValue={loja.chavePix ?? ''}
            dica="Pode ser o telefone, o CNPJ, o e-mail ou a chave aleatória. É o que a IA manda para o cliente pagar. Em branco, ela só oferece dinheiro e cartão na entrega."
            onBlur={(e) => {
              const v = e.target.value.trim()
              if (v !== (loja.chavePix ?? '')) {
                void mexer({ chavePix: v === '' ? null : v }, v === '' ? 'Chave Pix removida' : 'Chave Pix salva')
              }
            }}
          />

          <GrupoEscolhas nome="Quando imprimir a comanda" min={1} max={1} obrigatorio escolhidos={1}>
            <Escolha
              tipo="radio" nome="imprimir" valor="ACEITAR" rotulo="Ao aceitar o pedido"
              descricao="A cozinha só recebe o que a loja confirmou. Recomendado."
              marcada={loja.imprimirAo === 'ACEITAR'}
              onMudar={() => void mexer({ imprimirAo: 'ACEITAR' })}
            />
            <Escolha
              tipo="radio" nome="imprimir" valor="RECEBER" rotulo="Assim que o pedido chega"
              descricao="Mais rápido, mas imprime pedido que pode ser recusado."
              marcada={loja.imprimirAo === 'RECEBER'}
              onMudar={() => void mexer({ imprimirAo: 'RECEBER' })}
            />
          </GrupoEscolhas>

          {/*
            Fica logo abaixo de "quando imprimir" porque muda a mesma coisa:
            o instante em que a comanda sai. Com isto ligado, pedido do
            WhatsApp não espera clique nenhum.
          */}
          <Escolha
            tipo="checkbox"
            nome="aceitarAutoWhatsapp"
            valor="1"
            rotulo="Aceitar sozinho os pedidos do WhatsApp"
            descricao="A comanda sai na hora, sem ninguém clicar em Aceitar. Faz sentido porque nesse canal quem fechou foi a IA, que já conferiu o cardápio e confirmou o total com o cliente. Em troca, não há mais uma pessoa entre o pedido e a chapa: se faltar ingrediente, resta cancelar e ligar."
            marcada={loja.aceitarAutoWhatsapp}
            onMudar={(m) =>
              void mexer(
                { aceitarAutoWhatsapp: m },
                m ? 'Pedido do WhatsApp entra aceito' : 'Pedido do WhatsApp volta a esperar aceite',
              )
            }
          />

          <Escolha
            tipo="checkbox"
            nome="avisarStatusZap"
            valor="1"
            rotulo="Avisar o cliente no WhatsApp a cada etapa"
            descricao="Confirmado, na chapa, pronto, saiu para entrega e entregue. Texto fixo, uma mensagem por etapa. Não vale para pedido de balcão."
            marcada={loja.avisarStatusZap}
            onMudar={(m) =>
              void mexer({ avisarStatusZap: m }, m ? 'Avisos ligados' : 'Avisos desligados')
            }
          />
        </section>
      ) : null}

      {/*
        Seção, e não página no menu: o menu não filtra por papel, então
        "Fidelidade" apareceria para o ENTREGADOR e para a COZINHA. Aqui ela
        fica dentro do bloco de gestor, como o resto da configuração.
      */}
      {gestor ? (
        <section className="bloco-gestao">
          <SectionTitle variante="amarelo" tamanho="sm">Fidelidade</SectionTitle>

          <Escolha
            tipo="checkbox"
            nome="fidelidadeAtiva"
            valor="1"
            rotulo="Dar pontos para quem compra"
            descricao="Vale só para pedidos a partir de agora. Quem já comprou não ganha ponto pelo que passou. E o ponto só entra quando alguém marca o pedido como entregue."
            marcada={loja.fidelidadeAtiva}
            onMudar={(m) =>
              void mexer(
                { fidelidadeAtiva: m },
                m ? 'Fidelidade ligada' : 'Fidelidade desligada',
              )
            }
          />

          {loja.fidelidadeAtiva ? (
            <>
              <Input
                rotulo="Pontos por real gasto (de 1 a 10)"
                inputMode="numeric"
                defaultValue={String(loja.fidelidadePontosPorReal)}
                dica="Conta sobre o valor dos produtos. A taxa de entrega fica de fora, senão quem mora longe ganharia mais pelo mesmo lanche."
                onBlur={(e) => {
                  const v = Number(e.target.value)
                  if (v >= 1 && v <= 10 && v !== loja.fidelidadePontosPorReal) {
                    void mexer({ fidelidadePontosPorReal: v }, 'Régua de pontos salva')
                  }
                }}
              />
              {/*
                A data do corte, na tela. Ela existe no banco e no registro de
                auditoria, mas nenhuma tela do portal lê auditoria — e esta é a
                informação que resolve a discussão com o cliente que jura que
                comprou antes.
              */}
              {loja.fidelidadeAtivaDesde ? (
                <p className="nota-alerta">
                  A fidelidade está ligada desde{' '}
                  {new Date(loja.fidelidadeAtivaDesde).toLocaleDateString('pt-BR')}. Os pontos
                  valem a partir dessa data.
                </p>
              ) : null}
              <p className="dash-nota">
                Por enquanto o sistema só soma. Quando você decidir o que o cliente troca pelos
                pontos, a gente liga o resgate.
              </p>
            </>
          ) : null}
        </section>
      ) : null}
    </div>
  )
}
